import React, { useEffect, useState } from 'react';
import { CalendarIcon, DollarSignIcon, PlusIcon, XIcon } from 'lucide-react';
export function ContractForm({
  locale,
  contract,
  onSelectContract
}) {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    vendor: '',
    type: 'Service',
    description: '',
    startDate: '',
    endDate: '',
    value: '',
    autoRenew: false,
    renewalTerms: '',
    paymentTerms: 'Net 30',
    status: 'Draft',
    documents: [],
    notes: ''
  });
  // Initialize form data if editing an existing contract
  useEffect(() => {
    if (contract) {
      setFormData({
        ...contract,
        value: contract.value ? contract.value.toString() : '',
        documents: contract.documents || [],
        notes: contract.notes || ''
      });
    }
  }, [contract]);
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  const handleSubmit = e => {
    e.preventDefault();
    // In a real app, this would save the contract to the database
    console.log('Saving contract:', formData);
    // Show success message
    alert(`Contract ${formData.id || 'new'} saved successfully!`);
    // Clear form or navigate back to list
    if (!contract) {
      setFormData({
        id: '',
        name: '',
        vendor: '',
        type: 'Service',
        description: '',
        startDate: '',
        endDate: '',
        value: '',
        autoRenew: false,
        renewalTerms: '',
        paymentTerms: 'Net 30',
        status: 'Draft',
        documents: [],
        notes: ''
      });
    }
  };
  const handleFileChange = e => {
    const files = Array.from(e.target.files);
    const newDocuments = files.map(file => ({
      name: file.name,
      size: file.size,
      type: file.type,
      uploadDate: new Date().toISOString()
    }));
    setFormData({
      ...formData,
      documents: [...formData.documents, ...newDocuments]
    });
  };
  const removeDocument = index => {
    const updatedDocs = [...formData.documents];
    updatedDocs.splice(index, 1);
    setFormData({
      ...formData,
      documents: updatedDocs
    });
  };
  return <div data-id="element-2263">
      <div className="mb-8" data-id="element-2264">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-2265">
          {contract ? 'Edit Contract' : 'Add New Contract'}
        </h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-2266">
          {contract ? `Edit details for contract ${contract.id}` : 'Fill in the details below to create a new contract'}
        </p>
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-2267">
        <form onSubmit={handleSubmit} className="p-6" data-id="element-2268">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-2269">
            {/* Contract ID - only shown when editing */}
            {contract && <div className="sm:col-span-3" data-id="element-2270">
                <label htmlFor="id" className="block text-sm font-medium text-gray-700" data-id="element-2271">
                  Contract ID
                </label>
                <div className="mt-1" data-id="element-2272">
                  <input type="text" name="id" id="id" value={formData.id} disabled className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md bg-gray-50" data-id="element-2273" />
                </div>
              </div>}
            {/* Contract Name */}
            <div className="sm:col-span-3" data-id="element-2274">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-2275">
                Contract Name
              </label>
              <div className="mt-1" data-id="element-2276">
                <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2277" />
              </div>
            </div>
            {/* Vendor */}
            <div className="sm:col-span-3" data-id="element-2278">
              <label htmlFor="vendor" className="block text-sm font-medium text-gray-700" data-id="element-2279">
                Vendor
              </label>
              <div className="mt-1" data-id="element-2280">
                <input type="text" name="vendor" id="vendor" value={formData.vendor} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2281" />
              </div>
            </div>
            {/* Contract Type */}
            <div className="sm:col-span-3" data-id="element-2282">
              <label htmlFor="type" className="block text-sm font-medium text-gray-700" data-id="element-2283">
                Contract Type
              </label>
              <div className="mt-1" data-id="element-2284">
                <select id="type" name="type" value={formData.type} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2285">
                  <option value="Service" data-id="element-2286">Service</option>
                  <option value="Lease" data-id="element-2287">Lease</option>
                  <option value="License" data-id="element-2288">License</option>
                  <option value="Purchase" data-id="element-2289">Purchase</option>
                  <option value="Maintenance" data-id="element-2290">Maintenance</option>
                  <option value="Employment" data-id="element-2291">Employment</option>
                  <option value="Other" data-id="element-2292">Other</option>
                </select>
              </div>
            </div>
            {/* Status - only shown when editing */}
            {contract && <div className="sm:col-span-3" data-id="element-2293">
                <label htmlFor="status" className="block text-sm font-medium text-gray-700" data-id="element-2294">
                  Status
                </label>
                <div className="mt-1" data-id="element-2295">
                  <select id="status" name="status" value={formData.status} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2296">
                    <option value="Draft" data-id="element-2297">Draft</option>
                    <option value="Pending Review" data-id="element-2298">Pending Review</option>
                    <option value="Active" data-id="element-2299">Active</option>
                    <option value="Expiring Soon" data-id="element-2300">Expiring Soon</option>
                    <option value="Expired" data-id="element-2301">Expired</option>
                  </select>
                </div>
              </div>}
            {/* Description */}
            <div className="sm:col-span-6" data-id="element-2302">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-2303">
                Description
              </label>
              <div className="mt-1" data-id="element-2304">
                <textarea id="description" name="description" rows={3} value={formData.description} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2305" />
              </div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-2306">
                Brief description of the contract and its purpose
              </p>
            </div>
            {/* Start Date */}
            <div className="sm:col-span-3" data-id="element-2307">
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700" data-id="element-2308">
                Start Date
              </label>
              <div className="mt-1 relative rounded-md shadow-sm" data-id="element-2309">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2310">
                  <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-2311" />
                </div>
                <input type="date" name="startDate" id="startDate" value={formData.startDate} onChange={handleChange} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" data-id="element-2312" />
              </div>
            </div>
            {/* End Date */}
            <div className="sm:col-span-3" data-id="element-2313">
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700" data-id="element-2314">
                End Date
              </label>
              <div className="mt-1 relative rounded-md shadow-sm" data-id="element-2315">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2316">
                  <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-2317" />
                </div>
                <input type="date" name="endDate" id="endDate" value={formData.endDate} onChange={handleChange} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" data-id="element-2318" />
              </div>
            </div>
            {/* Contract Value */}
            <div className="sm:col-span-3" data-id="element-2319">
              <label htmlFor="value" className="block text-sm font-medium text-gray-700" data-id="element-2320">
                Contract Value
              </label>
              <div className="mt-1 relative rounded-md shadow-sm" data-id="element-2321">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2322">
                  <DollarSignIcon className="h-5 w-5 text-gray-400" data-id="element-2323" />
                </div>
                <input type="number" name="value" id="value" value={formData.value} onChange={handleChange} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="0.00" data-id="element-2324" />
              </div>
            </div>
            {/* Payment Terms */}
            <div className="sm:col-span-3" data-id="element-2325">
              <label htmlFor="paymentTerms" className="block text-sm font-medium text-gray-700" data-id="element-2326">
                Payment Terms
              </label>
              <div className="mt-1" data-id="element-2327">
                <select id="paymentTerms" name="paymentTerms" value={formData.paymentTerms} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2328">
                  <option value="Net 15" data-id="element-2329">Net 15</option>
                  <option value="Net 30" data-id="element-2330">Net 30</option>
                  <option value="Net 45" data-id="element-2331">Net 45</option>
                  <option value="Net 60" data-id="element-2332">Net 60</option>
                  <option value="Due on Receipt" data-id="element-2333">Due on Receipt</option>
                  <option value="Custom" data-id="element-2334">Custom</option>
                </select>
              </div>
            </div>
            {/* Auto Renewal */}
            <div className="sm:col-span-6" data-id="element-2335">
              <div className="flex items-start" data-id="element-2336">
                <div className="flex items-center h-5" data-id="element-2337">
                  <input id="autoRenew" name="autoRenew" type="checkbox" checked={formData.autoRenew} onChange={handleChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-2338" />
                </div>
                <div className="ml-3 text-sm" data-id="element-2339">
                  <label htmlFor="autoRenew" className="font-medium text-gray-700" data-id="element-2340">
                    Auto Renewal
                  </label>
                  <p className="text-gray-500" data-id="element-2341">
                    Enable if this contract automatically renews
                  </p>
                </div>
              </div>
            </div>
            {/* Renewal Terms - only shown if auto renewal is enabled */}
            {formData.autoRenew && <div className="sm:col-span-6" data-id="element-2342">
                <label htmlFor="renewalTerms" className="block text-sm font-medium text-gray-700" data-id="element-2343">
                  Renewal Terms
                </label>
                <div className="mt-1" data-id="element-2344">
                  <textarea id="renewalTerms" name="renewalTerms" rows={2} value={formData.renewalTerms} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="Specify renewal period, notice requirements, etc." data-id="element-2345" />
                </div>
              </div>}
            {/* Document Upload */}
            <div className="sm:col-span-6" data-id="element-2346">
              <label className="block text-sm font-medium text-gray-700" data-id="element-2347">
                Contract Documents
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md" data-id="element-2348">
                <div className="space-y-1 text-center" data-id="element-2349">
                  <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true" data-id="element-2350">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" data-id="element-2351" />
                  </svg>
                  <div className="flex text-sm text-gray-600" data-id="element-2352">
                    <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500" data-id="element-2353">
                      <span data-id="element-2354">Upload files</span>
                      <input id="file-upload" name="file-upload" type="file" className="sr-only" multiple onChange={handleFileChange} data-id="element-2355" />
                    </label>
                    <p className="pl-1" data-id="element-2356">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-2357">
                    PDF, DOC, DOCX, XLS, XLSX up to 10MB
                  </p>
                </div>
              </div>
            </div>
            {/* Document List */}
            {formData.documents.length > 0 && <div className="sm:col-span-6" data-id="element-2358">
                <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-2359">
                  Uploaded Documents
                </h4>
                <ul className="border border-gray-200 rounded-md divide-y divide-gray-200" data-id="element-2360">
                  {formData.documents.map((doc, index) => <li key={index} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm" data-id="element-2361">
                      <div className="w-0 flex-1 flex items-center" data-id="element-2362">
                        <svg className="flex-shrink-0 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-2363">
                          <path fillRule="evenodd" d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z" clipRule="evenodd" data-id="element-2364" />
                        </svg>
                        <span className="ml-2 flex-1 w-0 truncate" data-id="element-2365">
                          {doc.name}
                        </span>
                      </div>
                      <div className="ml-4 flex-shrink-0 flex items-center" data-id="element-2366">
                        <span className="text-xs text-gray-500 mr-4" data-id="element-2367">
                          {Math.round(doc.size / 1024)} KB
                        </span>
                        <button type="button" onClick={() => removeDocument(index)} className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-2368">
                          <XIcon className="h-4 w-4" data-id="element-2369" />
                        </button>
                      </div>
                    </li>)}
                </ul>
              </div>}
            {/* Notes */}
            <div className="sm:col-span-6" data-id="element-2370">
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700" data-id="element-2371">
                Notes
              </label>
              <div className="mt-1" data-id="element-2372">
                <textarea id="notes" name="notes" rows={3} value={formData.notes} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="Additional notes or comments about this contract" data-id="element-2373" />
              </div>
            </div>
          </div>
          <div className="pt-5 mt-6 border-t border-gray-200" data-id="element-2374">
            <div className="flex justify-end" data-id="element-2375">
              <button type="button" onClick={() => onSelectContract(null)} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-2376">
                Cancel
              </button>
              <button type="submit" className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-2377">
                Save
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>;
}